import type { CadDocument, Layer } from '../../model/document';
import { entityBox, type BlockDef, type CadEntity } from '../../model/entity';
import type { Box } from '../../model/geom';
import { radToDeg } from '../../model/units';
import type { ConversionWarning, WriteOptions } from '../adapter';
import { hexToAci, ACI_BY_LAYER } from './aci';
import { DxfWriterBuffer } from './groupCodes';

/**
 * DXF 라이터 (작업지시서 P3-03)
 *
 * ★ 왕복 무결성이 이 파일의 존재 이유다.
 *   원본 → 내부 JSON → DXF → 재파싱 에서 좌표가 **µm 단위로 완전 일치** 해야 한다.
 *   내부 좌표는 정수 µm 이고 DXF 에는 mm 로 나가므로(÷1000) 소수점 3자리면 무손실이다.
 *   `formatReal` 이 6자리까지 쓰므로 자릿수 손실은 없다.
 *
 * 출력 대상은 AC1021(AutoCAD 2007). 이 버전이 호환 범위가 가장 넓다 —
 * 구형 뷰어도 열리고 최신 AutoCAD 도 문제없이 읽는다.
 */

export function writeDxf(
  doc: CadDocument,
  opt: WriteOptions = {}
): { data: Uint8Array; warnings: ConversionWarning[] } {
  const warnings: ConversionWarning[] = [];
  const b = new DxfWriterBuffer();
  const version = opt.version ?? 'AC1021';
  const insUnits = opt.insUnits ?? 4; // mm

  // 핸들은 16진 문자열이어야 하고 도면 안에서 유일해야 한다.
  let handle = 0x100;
  const nextHandle = () => (handle++).toString(16).toUpperCase();

  const extents = documentExtents(doc);

  /* ------------------------------------------------------------ HEADER */
  b.pair(0, 'SECTION').pair(2, 'HEADER');
  b.pair(9, '$ACADVER').pair(1, version);
  b.pair(9, '$INSUNITS').pair(70, insUnits);
  b.pair(9, '$EXTMIN').pair(10, toMm(extents.minX)).pair(20, toMm(extents.minY)).pair(30, 0);
  b.pair(9, '$EXTMAX').pair(10, toMm(extents.maxX)).pair(20, toMm(extents.maxY)).pair(30, 0);
  // 현재 레이어 — 없는 이름을 쓰면 일부 뷰어가 경고를 낸다
  b.pair(9, '$CLAYER').pair(8, doc.layers[0]?.code ?? '0');
  b.pair(0, 'ENDSEC');

  /* ------------------------------------------------------------ TABLES */
  b.pair(0, 'SECTION').pair(2, 'TABLES');

  // LTYPE — 레이어가 선종류를 참조하므로 최소한 CONTINUOUS 는 있어야 한다
  b.pair(0, 'TABLE').pair(2, 'LTYPE').pair(5, nextHandle()).pair(100, 'AcDbSymbolTable').pair(70, 1);
  b.pair(0, 'LTYPE')
    .pair(5, nextHandle())
    .pair(100, 'AcDbSymbolTableRecord')
    .pair(100, 'AcDbLinetypeTableRecord')
    .pair(2, 'CONTINUOUS')
    .pair(70, 0)
    .pair(3, 'Solid line')
    .pair(72, 65)
    .pair(73, 0)
    .pair(40, 0);
  b.pair(0, 'ENDTAB');

  // LAYER
  b.pair(0, 'TABLE').pair(2, 'LAYER').pair(5, nextHandle()).pair(100, 'AcDbSymbolTable');
  b.pair(70, doc.layers.length);
  for (const l of doc.layers) writeLayer(b, l, nextHandle());
  b.pair(0, 'ENDTAB');

  b.pair(0, 'ENDSEC');

  /* ------------------------------------------------------------ BLOCKS */
  b.pair(0, 'SECTION').pair(2, 'BLOCKS');
  // 모델 공간·용지 공간 블록은 규격상 반드시 있어야 한다
  writeSystemBlock(b, '*Model_Space', nextHandle);
  writeSystemBlock(b, '*Paper_Space', nextHandle);
  for (const def of doc.blocks) writeBlock(b, def, nextHandle, warnings);
  b.pair(0, 'ENDSEC');

  /* ---------------------------------------------------------- ENTITIES */
  b.pair(0, 'SECTION').pair(2, 'ENTITIES');
  for (const e of doc.entities) writeEntity(b, e, nextHandle, warnings, '*Model_Space');
  b.pair(0, 'ENDSEC');

  b.pair(0, 'EOF');
  return { data: b.toBytes(), warnings };
}

/* -------------------------------------------------------------------------- *
 * 보조
 * -------------------------------------------------------------------------- */

/** µm 정수 → mm 실수. DXF 좌표는 도면 단위(=mm)로 나간다. */
function toMm(v: number): number {
  return v / 1000;
}

function documentExtents(doc: CadDocument): Box {
  if (doc.entities.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  const blocks = new Map(doc.blocks.map((b) => [b.code, b]));
  let box: Box | null = null;
  for (const e of doc.entities) {
    const b = entityBox(e, blocks);
    box = box
      ? {
          minX: Math.min(box.minX, b.minX),
          minY: Math.min(box.minY, b.minY),
          maxX: Math.max(box.maxX, b.maxX),
          maxY: Math.max(box.maxY, b.maxY),
        }
      : b;
  }
  return box ?? { minX: 0, minY: 0, maxX: 0, maxY: 0 };
}

function writeLayer(b: DxfWriterBuffer, l: Layer, h: string): void {
  b.pair(0, 'LAYER')
    .pair(5, h)
    .pair(100, 'AcDbSymbolTableRecord')
    .pair(100, 'AcDbLayerTableRecord')
    .pair(2, l.code)
    // flags: 1=frozen, 4=locked
    .pair(70, (l.visible ? 0 : 1) | (l.locked ? 4 : 0))
    // 색이 음수면 "꺼짐". 숨김 레이어를 이렇게 표현하는 것이 AutoCAD 관례다.
    .pair(62, l.visible ? hexToAci(l.color) : -hexToAci(l.color))
    .pair(6, 'CONTINUOUS')
    .pair(370, l.lineWeight)
    .pair(390, 'F'); // plot style handle (필수 아님이나 넣어두면 호환성이 좋다)
}

function writeSystemBlock(b: DxfWriterBuffer, name: string, next: () => string): void {
  b.pair(0, 'BLOCK')
    .pair(5, next())
    .pair(100, 'AcDbEntity')
    .pair(8, '0')
    .pair(100, 'AcDbBlockBegin')
    .pair(2, name)
    .pair(70, 0)
    .pair(10, 0)
    .pair(20, 0)
    .pair(30, 0)
    .pair(3, name)
    .pair(1, '');
  b.pair(0, 'ENDBLK').pair(5, next()).pair(100, 'AcDbEntity').pair(8, '0').pair(100, 'AcDbBlockEnd');
}

function writeBlock(
  b: DxfWriterBuffer,
  def: BlockDef,
  next: () => string,
  warnings: ConversionWarning[]
): void {
  b.pair(0, 'BLOCK')
    .pair(5, next())
    .pair(100, 'AcDbEntity')
    .pair(8, '0')
    .pair(100, 'AcDbBlockBegin')
    .pair(2, def.code)
    .pair(70, 0)
    .pair(10, 0)
    .pair(20, 0)
    .pair(30, 0)
    .pair(3, def.code)
    .pair(1, '');

  for (const e of def.entities) writeEntity(b, e, next, warnings, def.code);

  b.pair(0, 'ENDBLK').pair(5, next()).pair(100, 'AcDbEntity').pair(8, '0').pair(100, 'AcDbBlockEnd');
}

/** 모든 엔티티에 공통으로 붙는 머리 */
function head(
  b: DxfWriterBuffer,
  type: string,
  e: CadEntity,
  h: string,
  owner: string,
  subclass: string
): void {
  b.pair(0, type)
    .pair(5, h)
    .pair(100, 'AcDbEntity')
    .pair(8, e.layer)
    .pair(62, e.color ? hexToAci(e.color) : ACI_BY_LAYER);
  if (e.lineWeight != null) b.pair(370, e.lineWeight);
  b.pair(100, subclass);
  void owner;
}

function writeEntity(
  b: DxfWriterBuffer,
  e: CadEntity,
  next: () => string,
  warnings: ConversionWarning[],
  owner: string
): void {
  switch (e.type) {
    case 'line':
      head(b, 'LINE', e, next(), owner, 'AcDbLine');
      b.pair(10, toMm(e.a.x)).pair(20, toMm(e.a.y)).pair(30, 0);
      b.pair(11, toMm(e.b.x)).pair(21, toMm(e.b.y)).pair(31, 0);
      break;

    case 'polyline': {
      if (e.points.length < 2) return;
      head(b, 'LWPOLYLINE', e, next(), owner, 'AcDbPolyline');
      b.pair(90, e.points.length);
      b.pair(70, e.closed ? 1 : 0);
      for (const p of e.points) {
        b.pair(10, toMm(p.x)).pair(20, toMm(p.y));
      }
      break;
    }

    case 'circle':
      head(b, 'CIRCLE', e, next(), owner, 'AcDbCircle');
      b.pair(10, toMm(e.center.x)).pair(20, toMm(e.center.y)).pair(30, 0);
      b.pair(40, toMm(e.radius));
      break;

    case 'arc':
      head(b, 'ARC', e, next(), owner, 'AcDbCircle');
      b.pair(10, toMm(e.center.x)).pair(20, toMm(e.center.y)).pair(30, 0);
      b.pair(40, toMm(e.radius));
      b.pair(100, 'AcDbArc');
      b.pair(50, radToDeg(e.startAngle));
      b.pair(51, radToDeg(e.endAngle));
      break;

    case 'text':
      head(b, 'TEXT', e, next(), owner, 'AcDbText');
      b.pair(10, toMm(e.at.x)).pair(20, toMm(e.at.y)).pair(30, 0);
      b.pair(40, toMm(e.height));
      b.pair(1, e.text);
      if (e.rotation !== 0) b.pair(50, radToDeg(e.rotation));
      b.pair(72, e.align === 'center' ? 1 : e.align === 'right' ? 2 : 0);
      // 정렬을 쓰려면 두 번째 정렬점(11/21)이 있어야 AutoCAD 가 인식한다
      if (e.align !== 'left') {
        b.pair(11, toMm(e.at.x)).pair(21, toMm(e.at.y)).pair(31, 0);
      }
      break;

    case 'blockref':
      head(b, 'INSERT', e, next(), owner, 'AcDbBlockReference');
      b.pair(2, e.blockCode);
      b.pair(10, toMm(e.at.x)).pair(20, toMm(e.at.y)).pair(30, 0);
      b.pair(41, e.scale).pair(42, e.scale).pair(43, e.scale);
      if (e.rotation !== 0) b.pair(50, radToDeg(e.rotation));
      break;

    case 'dimension': {
      // 치수를 DXF DIMENSION 으로 내보내려면 익명 블록까지 만들어야 한다.
      // 도면 검토가 목적이므로 선분으로 분해해 내보낸다 — 모양은 보존되고,
      // 되읽을 때 치수 객체가 아니라 선이 된다는 점을 경고로 알린다.
      warnings.push({
        code: 'unsupported_entity',
        message: '치수는 선분으로 분해되어 저장됩니다. 다시 읽으면 치수 객체가 아닙니다.',
        subject: 'DIMENSION',
      });
      const seg = (a: { x: number; y: number }, c: { x: number; y: number }) => {
        head(b, 'LINE', e, next(), owner, 'AcDbLine');
        b.pair(10, toMm(a.x)).pair(20, toMm(a.y)).pair(30, 0);
        b.pair(11, toMm(c.x)).pair(21, toMm(c.y)).pair(31, 0);
      };
      seg(e.a, e.offsetPoint);
      seg(e.b, { x: e.offsetPoint.x + (e.b.x - e.a.x), y: e.offsetPoint.y + (e.b.y - e.a.y) });
      seg(e.offsetPoint, {
        x: e.offsetPoint.x + (e.b.x - e.a.x),
        y: e.offsetPoint.y + (e.b.y - e.a.y),
      });
      break;
    }
  }
}
